import * as THREE from 'three';
import type { BattleRules, BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { URO_NOSE } from '../models/uro.ts';
import { SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The hunt (El Cuerno 3, docs/SAGAS.md): the Old Uro's tracks across the tail, deep cloven prints from the camp to
 * where it grazes today, the last of them still smoking; its place dressed its own way (a shallow watering hole, a
 * ring of fallen standing stones, a patch of grey ash grass); the iron traps laid for it (rusted, the ones laid in an
 * outing before), and the hunt's moments: dust kicked back as it paws, a trail of it behind a charge, steam blown
 * from its nostrils, the snap of a trap, what it runs over. (The Uro itself is a nightmare: models/uro.ts.)
 *
 * Draw calls: one instanced print, one instanced trap, the place (one or two merged meshes), one glow layer. No lights.
 */

const K = WORLD_PER_HEX / 1000;
const MAX_TRAPS = 12;
const DUST = '#8a8478';
const ASH = '#9a9ca4';
const STEAM = '#d8dde4';
const EMBER = new THREE.Color('#ff4f3a');
const WATER = new THREE.Color('#9fc4dc');
const RUST = new THREE.Color('#c08a72');
type Hunt = NonNullable<BattleRules['hunt']>;
/** The Uro's scale on the board (engine/library.ts), for where its nose is. */
const URO_SCALE = SCALE.enemy * 1.55;

/** A cloven print: the two halves of the hoof pressed into the ground, a rim of turned earth behind. */
const printGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  const id = new THREE.Matrix4();
  const half = new THREE.CircleGeometry(0.075, 9).rotateX(-Math.PI / 2);
  for (const s of [-1, 1]) kit.add(half, '#17151a', id, place(s * 0.05, 0, 0.01, s * -0.12, new THREE.Vector3(0.65, 1, 1.25)));
  kit.add(new THREE.RingGeometry(0.11, 0.15, 10, 1, Math.PI * 1.15, Math.PI * 0.7).rotateX(-Math.PI / 2), '#3a3530', id, place(0, 0.002, 0.0));
  const mesh = kit.build(new THREE.MeshBasicMaterial(), 'uroPrint') as THREE.Mesh;
  (mesh.material as THREE.Material).dispose();
  return mesh.geometry;
};

/** A jaw trap: an iron ring, two toothed jaws open flat, the pan, a stake and a short chain to it. */
const trapGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  const id = new THREE.Matrix4();
  const iron = '#4a4f57';
  const dark = '#2a2d33';
  kit.add(new THREE.TorusGeometry(0.2, 0.022, 4, 16).rotateX(Math.PI / 2), dark, id, place(0, 0.025, 0));
  // The jaws, open: two half rings lying out either side, teeth up.
  for (const s of [-1, 1]) {
    kit.add(new THREE.TorusGeometry(0.2, 0.018, 4, 10, Math.PI).rotateX(Math.PI / 2), iron, id, place(0, 0.035, s * 0.06, s < 0 ? Math.PI : 0, new THREE.Vector3(1, 1, 0.9)));
    for (let i = 0; i < 7; i++) {
      const a = (i / 6) * Math.PI;
      kit.add(new THREE.ConeGeometry(0.018, 0.06, 4), '#6a7078', id, place(Math.cos(a) * 0.19, 0.065, s * (0.06 + Math.sin(a) * 0.18)));
    }
  }
  // The pan in the middle and its springs either end.
  kit.add(new THREE.CylinderGeometry(0.07, 0.07, 0.02, 8), dark, id, place(0, 0.03, 0));
  for (const s of [-1, 1]) kit.add(new THREE.BoxGeometry(0.18, 0.03, 0.05), iron, id, place(s * 0.28, 0.025, 0));
  // The stake and the chain to it.
  kit.add(new THREE.CylinderGeometry(0.025, 0.012, 0.22, 5), '#3e3229', id, place(0.48, 0.08, 0.1, 0, 1, 0, -0.2));
  for (let i = 0; i < 3; i++) kit.add(new THREE.TorusGeometry(0.028, 0.008, 3, 6), iron, id, place(0.36 + i * 0.045, 0.02, 0.03 + i * 0.025, i * 1.2, 1, Math.PI / 2));
  const mesh = kit.build(new THREE.MeshBasicMaterial(), 'uroTrap') as THREE.Mesh;
  (mesh.material as THREE.Material).dispose();
  return mesh.geometry;
};

/** Small deterministic noise for laying things out (same layout every outing). */
const rnd = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export class HuntLayer {
  readonly group = new THREE.Group();
  private prints: THREE.InstancedMesh;
  private printMat: THREE.MeshBasicMaterial;
  private traps: THREE.InstancedMesh;
  private trapMat: THREE.MeshStandardMaterial;
  private place: THREE.Mesh[] = [];
  private placeMats: THREE.Material[] = [];
  private glow: GlowLayer;
  /** The last prints (world), the ones still smoking. */
  private hot: THREE.Vector3[] = [];
  private home: THREE.Vector3;
  /** The traps drawn, by id (and whether they were laid before), and where. */
  private shown = new Map<number, { p: THREE.Vector3; old: boolean; yaw: number }>();
  private now = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3(1, 1, 1);

  constructor(
    private ctx: OmenCtx,
    private r: Hunt,
  ) {
    this.group.name = 'hunt';
    const low = ctx.low;
    this.home = ctx.at(r.home.x, r.home.y, 0);
    // The tracks: from a little out of the camp to its place, wandering a little, left and right hooves in turn.
    const from = ctx.at(0, 0, 0);
    const to = this.home;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const len = Math.hypot(dx, dz) || 1;
    const ux = dx / len;
    const uz = dz / len;
    const step = 0.42;
    // Out past the camp's paving (its floor reaches about two hexes from the node).
    const start = 2.9;
    const end = len - 1.0;
    const n = Math.max(0, Math.floor((end - start) / step));
    const rng = rnd(Math.round(r.home.x * 7 + r.home.y * 13));
    this.printMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.62, depthWrite: false });
    this.prints = new THREE.InstancedMesh(printGeo(), this.printMat, Math.max(1, n));
    this.prints.renderOrder = 5;
    this.prints.frustumCulled = false;
    const yaw = Math.atan2(ux, uz);
    for (let i = 0; i < n; i++) {
      const d = start + i * step;
      const wob = Math.sin(d * 0.9 + rng() * 0.3) * 0.35;
      const side = i % 2 ? 0.17 : -0.17;
      const x = from.x + ux * d + uz * (wob + side);
      const z = from.z + uz * d - ux * (wob + side);
      this.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw + (rng() - 0.5) * 0.3);
      this.m.compose(this.v.set(x, ctx.ground(x, z) + 0.035, z), this.q, this.s3.setScalar(1.1 + rng() * 0.15));
      this.prints.setMatrixAt(i, this.m);
      if (i >= n - 5) this.hot.push(new THREE.Vector3(x, ctx.ground(x, z) + 0.05, z));
    }
    this.prints.count = n;
    this.group.add(this.prints);
    this.buildPlace(r.place, rng);
    this.trapMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.35, flatShading: true });
    this.traps = new THREE.InstancedMesh(trapGeo(), this.trapMat, MAX_TRAPS);
    this.traps.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.traps.count = 0;
    this.traps.frustumCulled = false;
    this.group.add(this.traps);
    this.glow = new GlowLayer(low ? 12 : 20);
    this.group.add(this.glow.mesh);
  }

  /** Its place, dressed: 0 the watering hole, 1 the fallen stones, 2 the ash pasture. */
  private buildPlace(which: number, rng: () => number): void {
    const h = this.home;
    const at = (x: number, z: number, lift = 0): THREE.Matrix4 => place(h.x + x, this.ctx.ground(h.x + x, h.z + z) + lift, h.z + z);
    const kit = new Kit();
    const id = new THREE.Matrix4();
    const add = (geo: THREE.BufferGeometry, color: string, m: THREE.Matrix4): void => kit.add(geo, color, id, m);
    const S = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
    if (which === 0) {
      // A shallow pool, a ring of trodden mud round it, reeds on the far side, prints at the water's edge.
      const R = 0.95;
      add(new THREE.CircleGeometry(R + 0.35, 24).rotateX(-Math.PI / 2), '#3a342e', at(0, 0, 0.03));
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2 + rng() * 0.4;
        add(new THREE.DodecahedronGeometry(0.1 + rng() * 0.08, 0), '#6a6f76', place(h.x + Math.cos(a) * (R + 0.25), this.ctx.ground(h.x, h.z) + 0.05, h.z + Math.sin(a) * (R + 0.25), a, S(1.3, 0.55, 1)));
      }
      for (let i = 0; i < 22; i++) {
        const a = -0.6 + (i / 22) * 2.2 + rng() * 0.1;
        const r = R + 0.05 + rng() * 0.25;
        const tall = 0.35 + rng() * 0.35;
        add(new THREE.ConeGeometry(0.018, tall, 3), i % 3 ? '#55684a' : '#6d7b5a', place(h.x + Math.cos(a) * r, this.ctx.ground(h.x, h.z) + tall / 2, h.z + Math.sin(a) * r, 0, 1, (rng() - 0.5) * 0.3, (rng() - 0.5) * 0.3));
      }
      // The water itself: its own glassy material.
      const wm = new THREE.MeshStandardMaterial({ color: '#2b4656', roughness: 0.15, metalness: 0.2, emissive: '#0d1a22', emissiveIntensity: 0.5 });
      const water = new THREE.Mesh(new THREE.CircleGeometry(R, 24).rotateX(-Math.PI / 2), wm);
      water.position.set(h.x, this.topOf(h.x, h.z, R) + 0.05, h.z);
      water.renderOrder = 4;
      this.group.add(water);
      this.place.push(water);
      this.placeMats.push(wm);
    } else if (which === 1) {
      // A ring of old standing stones, two fallen and one leaning, lichen on them; trodden ground in the middle.
      // Trodden ground in the middle: a few low flat stones, laid on the slope as it is (a disc would float or clip).
      for (let i = 0; i < 7; i++) add(new THREE.DodecahedronGeometry(0.16 + rng() * 0.08, 0), '#55575d', at((rng() - 0.5) * 1.6, (rng() - 0.5) * 1.6, 0.01).multiply(new THREE.Matrix4().makeScale(1.4, 0.18, 1.1)));
      const N = 7;
      for (let i = 0; i < N; i++) {
        const a = (i / N) * Math.PI * 2 + 0.2;
        const R = 2.0;
        const x = Math.cos(a) * R;
        const z = Math.sin(a) * R;
        const tall = 0.75 + rng() * 0.45;
        const fallen = i === 2 || i === 5;
        const lean = i === 4 ? 0.35 : (rng() - 0.5) * 0.12;
        const g = new THREE.BoxGeometry(0.34, tall, 0.22, 1, 2, 1);
        const col = i % 2 ? '#6c7076' : '#62666d';
        if (fallen) add(g, col, place(h.x + x, this.ctx.ground(h.x + x, h.z + z) + 0.11, h.z + z, a + 0.8, 1, Math.PI / 2, 0));
        else {
          add(g, col, place(h.x + x, this.ctx.ground(h.x + x, h.z + z) + tall / 2 - 0.05, h.z + z, -a + Math.PI / 2, 1, lean, 0));
          add(new THREE.CircleGeometry(0.06 + rng() * 0.04, 6), '#9fb2a4', place(h.x + x * 0.94, this.ctx.ground(h.x + x, h.z + z) + tall * 0.6, h.z + z * 0.94, -a - Math.PI / 2));
        }
      }
      // A cairn of small stones in the middle, where the clan used to leave its offerings.
      for (let i = 0; i < 6; i++) add(new THREE.DodecahedronGeometry(0.1 - i * 0.008, 0), '#7a7e86', at((rng() - 0.5) * 0.2, (rng() - 0.5) * 0.2, 0.06 + i * 0.07));
    } else {
      // A patch of grey ash grass, burnt down to stubble in places, a few blades still glowing at the tips.
      // (No ground disc: on the tail's slope it floats or clips; the grass alone makes the patch.)
      const n = this.ctx.low ? 80 : 150;
      for (let i = 0; i < n; i++) {
        const a = rng() * Math.PI * 2;
        const r = Math.sqrt(rng()) * 1.75;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        const tall = 0.16 + rng() * 0.26;
        const ember = i % 13 === 0;
        add(new THREE.ConeGeometry(0.045, tall, 3), ember ? '#c0543c' : rng() < 0.5 ? '#8a8c92' : '#a3a5ab', place(h.x + x, this.ctx.ground(h.x + x, h.z + z) + tall / 2, h.z + z, rng() * 3, 1, (rng() - 0.5) * 0.5, (rng() - 0.5) * 0.5));
      }
      // Bones of a boar it caught here: a skull of stone and a few broken chunks.
      add(new THREE.DodecahedronGeometry(0.16, 0), '#727d8a', at(0.7, -0.4, 0.08));
      for (let i = 0; i < 3; i++) add(new THREE.DodecahedronGeometry(0.08, 0), '#6a7480', at(0.95 + i * 0.12, -0.2 + i * 0.1, 0.04));
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, flatShading: true });
    const mesh = kit.build(mat, 'uroPlace') as THREE.Mesh | null;
    if (mesh) {
      mesh.frustumCulled = false;
      this.group.add(mesh);
      this.place.push(mesh);
      this.placeMats.push(mat);
    } else mat.dispose();
  }

  /** The highest ground under a disc (a floor laid over it sits just above, with no seam). */
  private topOf(x: number, z: number, r: number): number {
    let top = -Infinity;
    for (let i = 0; i <= 16; i++) for (const k of [0, 0.5, 1]) top = Math.max(top, this.ctx.ground(x + Math.cos((i / 16) * Math.PI * 2) * r * k, z + Math.sin((i / 16) * Math.PI * 2) * r * k));
    return top;
  }

  /** Where its nostrils are, from the sim (world). */
  private nose(e: { x: number; y: number; facing: number }): THREE.Vector3 {
    const a = (e.facing / 1024) * Math.PI * 2;
    const f = (URO_NOSE.z * URO_SCALE) / K;
    return this.ctx.at(e.x + Math.cos(a) * f, e.y + Math.sin(a) * f, URO_NOSE.y * URO_SCALE * 0.9);
  }

  /** Behind it, at its hooves (world). */
  private behind(e: { x: number; y: number; facing: number }, k = 1): THREE.Vector3 {
    const a = (e.facing / 1024) * Math.PI * 2;
    const f = (1.1 * URO_SCALE * k) / K;
    return this.ctx.at(e.x - Math.cos(a) * f, e.y - Math.sin(a) * f, 0.1);
  }

  private steam(e: { x: number; y: number; facing: number }, n: number): void {
    const p = this.nose(e);
    const a = (e.facing / 1024) * Math.PI * 2;
    // Two puffs, one from each nostril (sim (x, y) is world (x, z), so the side is the facing turned a quarter).
    for (const sd of [-1, 1]) {
      this.ctx.vfx.burst(this.v.set(p.x - Math.sin(a) * sd * 0.12, p.y, p.z + Math.cos(a) * sd * 0.12), { n, color: STEAM, speed: 0.012, up: 0.004, size: 0.3, life: 26, smoke: true, gravity: -0.0015, spread: 0.12 });
    }
  }

  /** Every fresh sim tick: the traps as they lie now, the dust, steam and smoke of what the Uro is doing. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const st = s.hunt;
    const u = s.enemies.find((e) => e.kind === 'uro' && e.hp > 0);
    for (const e of s._events ?? []) {
      if (e.t === 'die' && e.kind === 'uro') {
        // It crumbles to dust, as every nightmare does: a heap of grey ash, embers going out in it.
        const p = this.ctx.at(e.x, e.y, 0.5);
        this.ctx.vfx.burst(p, { n: low ? 10 : 22, color: ASH, speed: 0.03, up: 0.03, size: 0.6, life: 60, smoke: true, spread: 0.9 });
        this.ctx.vfx.burst(p, { n: low ? 10 : 20, color: '#ff8a6e', speed: 0.04, up: 0.06, size: 0.06, life: 40, gravity: -0.001, spread: 0.8 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.4, 3.0, '#c8ccd4', 26, 0.5);
        this.ctx.shake(0.22);
        continue;
      }
      if (e.t !== 'hunt') continue;
      if (e.kind === 'trap') {
        const p = this.ctx.at(e.x, e.y, 0.05);
        this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: DUST, speed: 0.012, up: 0.02, size: 0.2, life: 18, smoke: true, spread: 0.25 });
        this.ctx.vfx.ring(p, 0.1, 0.6, '#c8ccd4', 10, 0.5);
      } else if (e.kind === 'caught') {
        // The jaws slam shut: sparks off the iron, a ring of dust, the ground jolts.
        const p = this.ctx.at(e.x, e.y, 0.25);
        this.ctx.flare(p, '#e8f0ff', 2.4, 10);
        this.ctx.vfx.burst(p, { n: low ? 8 : 16, color: '#ffd6a0', speed: 0.06, up: 0.05, size: 0.05, life: 14, gravity: 0.006, spread: 0.3 });
        this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: DUST, speed: 0.02, up: 0.02, size: 0.4, life: 26, smoke: true, spread: 0.3 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.2, 1.8, '#dfe9ff', 14, 0.7);
        this.ctx.shake(0.12);
      } else if (e.kind === 'wind' && u) {
        this.ctx.vfx.burst(this.behind(u, -0.55), { n: low ? 2 : 4, color: DUST, speed: 0.02, up: 0.02, size: 0.3, life: 20, smoke: true, spread: 0.2 });
      } else if (e.kind === 'charge' && u) {
        this.ctx.vfx.ring(this.ctx.at(u.x, u.y, 0.05), 0.4, 2.2, '#c8ccd4', 16, 0.5);
        this.ctx.shake(0.08);
        this.steam(u, low ? 1 : 2);
      } else if (e.kind === 'snort' && u) {
        this.steam(u, low ? 2 : 3);
      } else if (e.kind === 'gore' || e.kind === 'trample') {
        const p = this.ctx.at(e.x, e.y, 0.3);
        this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: DUST, speed: 0.03, up: 0.03, size: 0.35, life: 22, smoke: true, spread: 0.3 });
        this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#6a6f76', speed: 0.05, up: 0.05, size: 0.07, life: 18, gravity: 0.008, spread: 0.3 });
        if (e.kind === 'gore') this.ctx.shake(0.1);
      }
    }
    if (u && st) {
      if (st.mode === 'wind' && (s.tick + u.id) % 3 === 0) this.ctx.vfx.burst(this.behind(u, -0.45), { n: 1, color: DUST, speed: 0.02, up: 0.025, size: 0.25, life: 16, smoke: true, spread: 0.15 });
      if (st.mode === 'charge' && s.tick % (low ? 3 : 2) === 0) this.ctx.vfx.burst(this.behind(u), { n: low ? 1 : 2, color: DUST, speed: 0.012, up: 0.02, size: 0.42, life: 30, smoke: true, spread: 0.35 });
      // Blowing: a puff of steam with each heave.
      if (st.mode === 'rest' && u.stun <= 0 && (s.tick + u.id) % 14 === 0) this.steam(u, 1);
      // Caught: it tears at the trap, throwing up dirt.
      if (u.stun > 0 && (s.tick + u.id) % 5 === 0) this.ctx.vfx.burst(this.ctx.at(u.x, u.y, 0.15), { n: low ? 1 : 2, color: DUST, speed: 0.03, up: 0.03, size: 0.25, life: 16, smoke: true, spread: 0.3 });
    }
    // The last prints still smoke.
    if (this.hot.length && s.tick % (low ? 18 : 10) === 0) {
      const p = this.hot[(s.tick / 10) % this.hot.length | 0] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: 1, color: '#5a5660', speed: 0.004, up: 0.02, size: 0.22, life: 30, smoke: true, gravity: -0.001, spread: 0.05 });
    }
    // The traps: drawn again only when they change.
    const now = st?.traps ?? this.r.traps;
    const ids = new Set(now.map((t) => t.id * 2 + (t.old ? 1 : 0)));
    let changed = ids.size !== this.shown.size;
    for (const k of ids) if (!this.shown.has(k)) changed = true;
    if (!changed) return;
    this.shown.clear();
    for (const t of now) this.shown.set(t.id * 2 + (t.old ? 1 : 0), { p: this.ctx.at(t.x, t.y, 0), old: t.old, yaw: ((t.id * 2.39 + t.x * 0.001) % (Math.PI * 2)) });
    let n = 0;
    for (const t of this.shown.values()) {
      if (n >= MAX_TRAPS) break;
      this.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), t.yaw);
      this.m.compose(this.v.set(t.p.x, this.ctx.ground(t.p.x, t.p.z) + 0.01, t.p.z), this.q, this.s3.setScalar(1.25));
      this.traps.setMatrixAt(n, this.m);
      this.traps.setColorAt(n, t.old ? RUST : new THREE.Color('#ffffff'));
      n++;
    }
    this.traps.count = n;
    this.traps.instanceMatrix.needsUpdate = true;
    if (this.traps.instanceColor) this.traps.instanceColor.needsUpdate = true;
  }

  /** Every redraw: the glints that keep the traps and the smoking prints in sight. */
  draw(): void {
    this.glow.begin();
    for (const t of this.shown.values()) this.glow.add(t.p.x, this.ctx.ground(t.p.x, t.p.z) + 0.12, t.p.z, t.old ? RUST : WATER, 0.9, 0.1 + 0.04 * Math.sin(this.now * 2.2 + t.yaw));
    for (const p of this.hot) this.glow.add(p.x, p.y, p.z, EMBER, 0.3, 0.1 + 0.05 * Math.sin(this.now * 3 + p.x));
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    this.prints.geometry.dispose();
    this.prints.dispose();
    this.printMat.dispose();
    this.traps.geometry.dispose();
    this.traps.dispose();
    this.trapMat.dispose();
    for (const m of this.place) m.geometry.dispose();
    for (const m of this.placeMats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
